/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Directive, OnInit } from '@angular/core';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { AnimatedGifHelper } from '../../helpers/animated-gif-helper';
import { FileUploadHelper } from '../../helpers/file-upload-helper';
import { ImageCropResult, ImageCropperData } from '../../models/images/image-crop.model';

@Directive()
export abstract class ImageCropDialogBase implements OnInit {
  imageFile?: File;
  imageBase64?: string;

  maintainAspectRatio = false;
  aspectRatio = 1;
  roundCropper = false;
  resizeToWidth = 0;
  resizeToHeight = 0;
  imageFormat: 'jpeg' | 'png' | 'gif' = 'jpeg';
  backgroundColor: string | undefined = 'white';
  description: string;
  isAnimatedGif = false;

  private croppedImageFile?: File;
  private croppedImageDataUrl?: string;

  protected abstract get cropData(): ImageCropperData;

  protected abstract detectChanges(): void;

  protected abstract closeWith(result?: ImageCropResult): void;

  ngOnInit(): void {
    const data = this.cropData;

    this.croppedImageFile = data.imageFile;
    this.setInputImage(data.imageFile);
    this.maintainAspectRatio = data.cropperShape === 'square' || data.cropperShape === 'circle' || !!data.aspectRatio;
    this.roundCropper = data.cropperShape === 'circle';
    this.resizeToWidth = data.resizeToWidth ? data.resizeToWidth : 0;
    this.resizeToHeight = data.resizeToHeight ? data.resizeToHeight : 0;

    if (data.imageFormat === 'png') {
      this.imageFormat = 'png';
      this.backgroundColor = undefined;
    }

    if (data.imageFile?.type.includes('gif')) {
      this.imageFormat = 'gif';
    }

    if (data.aspectRatio && data.cropperShape === 'rectangle') {
      this.aspectRatio = data.aspectRatio;
    }
    this.description = data.description;
  }

  updateCroppedImage(event: ImageCroppedEvent): void {
    const base64 = event.base64;

    if (this.imageFormat.includes('gif')) {
      const file = this.cropData.imageFile;
      AnimatedGifHelper.isGifAnimated(file).then((isAnimated) => {
        this.isAnimatedGif = isAnimated;
        AnimatedGifHelper.readFileAsDataURL(file);
        setTimeout(() => {
          this.imageBase64 = AnimatedGifHelper.imageBase64;
          this.croppedImageDataUrl = this.imageBase64;
          this.detectChanges();
        }, 100);
      });
    } else {
      if (base64) {
        this.croppedImageDataUrl = base64;
      } else if (event.blob) {
        const reader = new FileReader();
        reader.readAsDataURL(event.blob);
        reader.onloadend = () => {
          this.croppedImageDataUrl = reader.result as string;
        };
      }
    }
  }

  saveCrop(): void {
    if (this.croppedImageDataUrl) {
      const cropedImage = FileUploadHelper.convertDataUrlToFile(
        this.croppedImageDataUrl,
        this.croppedImageFile.name.toLowerCase(),
        'image/' + this.imageFormat,
      );
      if (cropedImage) {
        this.croppedImageFile = cropedImage;
      }
    }

    this.closeWith({
      croppedImageFile: this.croppedImageFile,
      croppedImageDataUrl: this.croppedImageDataUrl,
    });
  }

  cancelCrop(): void {
    this.closeWith();
  }

  private setInputImage(imageFile: File): void {
    if (imageFile?.type.includes('svg')) {
      const canvasEl = document.createElement('canvas') as HTMLCanvasElement;
      const context = canvasEl.getContext('2d');
      const image = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        image.onload = () => {
          canvasEl.width = image.naturalWidth;
          canvasEl.height = image.naturalHeight;
          if (context) {
            context?.drawImage(image, 0, 0);
          }
          this.croppedImageDataUrl = this.imageBase64 = canvasEl.toDataURL('image/png');
        };

        if (event.target) {
          image.src = event.target.result as string;
        }
      };

      reader.readAsDataURL(imageFile);
    } else {
      this.imageFile = imageFile;
    }
  }
}
